/**
 * Server-authoritative story source. Talks to @brhack/game-api, which verifies
 * the Telegram Mini App `initData` and runs the shared game engine. The browser
 * only forwards `initData` (opaque, signed) and renders what the server returns;
 * it never scores anything itself.
 */

import type { ChooseResult, StorySnapshot, StorySource } from './source.js';
import type { Ending, Localized, ReactionAnimation } from './caseModel.js';

interface ServerSnapshot {
  readonly scene: {
    readonly id: string;
    readonly title: Localized;
    readonly body: Localized;
    readonly ending: Ending;
  };
  readonly choices: ReadonlyArray<{
    readonly id: string;
    readonly label: Localized;
    readonly reaction: Localized;
    readonly command?: Localized;
  }>;
  readonly isEnding: boolean;
  readonly reaction?: Localized;
  readonly evidence?: { readonly count: number; readonly total: number };
  readonly progress: { readonly score: number; readonly revision: number; readonly status: string };
}

function toSnapshot(server: ServerSnapshot): StorySnapshot {
  const base = {
    scene: server.scene,
    choices: server.choices.map((choice) => {
      const entry = { id: choice.id, label: choice.label };
      return choice.command === undefined ? entry : { ...entry, command: choice.command };
    }),
    isEnding: server.isEnding,
    evidence: server.evidence ?? { count: 0, total: 0 },
  };
  return server.reaction === undefined ? base : { ...base, reaction: server.reaction };
}

export class RemoteStorySource implements StorySource {
  private readonly baseUrl: string;
  private readonly initData: string;
  private revision = 0;
  private lastScore = 0;

  constructor(baseUrl: string, initData: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.initData = initData;
  }

  async start(): Promise<StorySnapshot> {
    const server = await this.post('/api/session', {});
    this.revision = server.progress.revision;
    this.lastScore = server.progress.score;
    return toSnapshot(server);
  }

  async choose(choiceId: string): Promise<ChooseResult> {
    const previousScore = this.lastScore;
    const server = await this.post('/api/choice', { choiceId, revision: this.revision });
    this.revision = server.progress.revision;
    this.lastScore = server.progress.score;
    const delta = server.progress.score - previousScore;
    const reaction: ReactionAnimation | undefined =
      delta > 0 ? 'react-positive' : delta < 0 ? 'react-negative' : undefined;
    const snapshot = toSnapshot(server);
    return reaction === undefined ? { snapshot } : { snapshot, reaction };
  }

  restart(): Promise<StorySnapshot> {
    return this.start();
  }

  private async post(path: string, body: Record<string, unknown>): Promise<ServerSnapshot> {
    const response = await fetch(this.baseUrl + path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ initData: this.initData, ...body }),
    });
    if (!response.ok) {
      throw new Error(`game-api ${path} responded ${String(response.status)}`);
    }
    return (await response.json()) as ServerSnapshot;
  }
}
